import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, LineChart, Line } from "recharts";
import { TrendingUp, Euro, Target, Percent, Users, Handshake } from "lucide-react";
import { eur } from "@/components/bp/EarningsBoard";
import { BpSalesRanking } from "@/components/admin/bp/BpSalesRanking";

const pct = (v) => (v === null || v === undefined ? "—" : `${Number(v).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} %`);
const monthLabel = (k) => new Date(`${k}-01T00:00:00`).toLocaleDateString("fr-FR", { month: "short", year: "2-digit" });

/** Admin — tableau de bord « Ventes partenaires » : CA apporté, commissions, conversion lead → client, évolution mensuelle, classement. */
export const BpSalesDashboardTab = ({ refreshKey, onOpenBp }) => {
  const [data, setData] = useState(null);
  const [months, setMonths] = useState(12);
  useEffect(() => {
    api.get("/admin/bp/dashboard", { params: { months } }).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [months, refreshKey]);
  if (!data) return <div className="p-6 text-xs text-[#868E96]" data-testid="bp-sales-loading">Chargement du tableau de bord…</div>;
  const t = data.totals;
  const kpis = [
    [Euro, "CA apporté (1ᵉʳˢ paiements)", eur(t.revenue), "#002FA7", "revenue", `≈ ${eur(t.arr)} annualisés`],
    [Handshake, "Clients activés", t.sales, "#059669", "sales", `${t.rejected} refus · ${t.partners_selling}/${t.partners} partenaires vendeurs`],
    [Target, "Conversion lead → client", pct(t.conversion), "#7C3AED", "conversion", `${t.leads_won}/${t.leads} leads gagnés`],
    [TrendingUp, "Commissions dues", eur(t.commission_total), "#F59E0B", "commissions", `${eur(t.commissions.pending_validation)} à valider · ${eur(t.commissions.validated)} validées`],
    [Euro, "Commissions versées", eur(t.paid), "#059669", "paid", "cumul des versements effectués"],
    [Percent, "Poids des commissions", pct(t.commission_ratio), "#0A0A0A", "ratio", "commissions / CA apporté"],
  ];
  const series = data.series.map((s) => ({ ...s, label: monthLabel(s.month) }));
  return (
    <div className="space-y-6" data-testid="bp-sales-tab">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="bp-sales-kpis">
        {kpis.map(([Icon, label, val, color, tid, sub]) => (
          <div key={tid} className="bg-white border border-[#DEE2E6] p-4" data-testid={`bp-sales-kpi-${tid}`}>
            <div className="flex items-center justify-between gap-2"><div className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color }}>{label}</div><Icon className="h-4 w-4 shrink-0" style={{ color }} /></div>
            <div className="font-display font-black text-xl md:text-2xl mt-1" data-testid={`bp-sales-kpi-${tid}-value`}>{val}</div>
            <div className="text-[10px] text-[#868E96] mt-0.5">{sub}</div>
          </div>
        ))}
      </div>
      <div className="grid xl:grid-cols-2 gap-6">
        <div className="bg-white border border-[#DEE2E6] p-4" data-testid="bp-sales-chart-revenue">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold">CA apporté & commissions par mois</div>
            <select value={months} onChange={(e) => setMonths(Number(e.target.value))} className="h-7 border border-[#DEE2E6] bg-white px-2 text-[11px]" data-testid="bp-sales-months">{[6, 12, 24].map((m) => <option key={m} value={m}>{`${m} mois`}</option>)}</select>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={series} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F3F5" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v, n) => [eur(v), n]} /><Legend wrapperStyle={{ fontSize: 10 }} />
              <Bar dataKey="revenue" name="CA apporté" fill="#002FA7" radius={[2, 2, 0, 0]} /><Bar dataKey="commissions" name="Commissions" fill="#FFD700" radius={[2, 2, 0, 0]} /><Bar dataKey="paid" name="Versées" fill="#059669" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-[#DEE2E6] p-4" data-testid="bp-sales-chart-leads">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold mb-3">Leads déclarés, gagnés & ventes par mois</div>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={series} margin={{ top: 4, right: 8, left: -24, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F3F5" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip /><Legend wrapperStyle={{ fontSize: 10 }} />
              <Line type="monotone" dataKey="leads" name="Leads" stroke="#7C3AED" strokeWidth={2} dot={false} /><Line type="monotone" dataKey="leads_won" name="Gagnés" stroke="#059669" strokeWidth={2} dot={false} /><Line type="monotone" dataKey="sales" name="Ventes" stroke="#002FA7" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      <BpSalesRanking rows={data.partners} onOpenBp={onOpenBp} />
      <div className="text-[10px] text-[#868E96] inline-flex items-center gap-1"><Users className="h-3 w-3" />CA apporté = montant du 1ᵉʳ paiement de chaque client attribué (commissions refusées exclues) ; annualisé ×12 pour les abonnements mensuels. Généré le {new Date(data.generated_at).toLocaleString("fr-FR")}.</div>
    </div>
  );
};
